import { Component, inject, Input, OnInit, signal } from '@angular/core';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { GroupedAddressModel, LocationAddressModel, LocationService } from '@railmybox/api-booking';
import { tap } from 'rxjs';

export type LocationType = 'start' | 'destination';

@Component({
  selector: 'rmb-location-search',
  templateUrl: './location-search.component.html',
  styleUrls: ['./location-search.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(LocationSearchComponent)],
  standalone: false,
})
export class LocationSearchComponent extends AbstractValueAccessorComponent<LocationAddressModel> implements OnInit {
  private locationService = inject(LocationService);

  @Input() selectedAddress: LocationAddressModel;
  @Input() locationType: LocationType;
  @Input() baseUrl: string;
  @Input() enableFavorites = true;
  groupedAddresses = signal<GroupedAddressModel[]>([]);

  get selectedImage(): string {
    const selectedImage = this.selectedAddress
      ? `/assets/icons/waypoints/location_${this.selectedAddress.type.toLowerCase()}.svg`
      : `/assets/icons/${this.locationType}_card_empty.svg`;

    return this.baseUrl ? `${this.baseUrl}${selectedImage}` : selectedImage;
  }

  get baseUrlPath(): string {
    return this.baseUrl || '';
  }

  ngOnInit(): void {
    if (this.enableFavorites) {
      this.groupedAddresses.set([]);
      this.locationService.getLocations({ favorites: true }).subscribe((groupedAddresses) => {
        this.groupedAddresses.set(groupedAddresses);
      });
    }
  }

  onAddressSelect(address: LocationAddressModel): void {
    this.selectedAddress = { ...address };
    this.onChange?.(address);
  }

  onSearchValueChanged(searchTerm: string): void {
    if ((!searchTerm || searchTerm.trim() === '') && this.enableFavorites) {
      this.groupedAddresses.set([]);
      this.locationService.getLocations({ favorites: true }).subscribe((groupedAddresses) => {
        this.groupedAddresses.set(groupedAddresses);
      });
      return;
    }
    this.locationService.getLocations({ query: searchTerm }).subscribe((groupedAddresses) => {
      this.groupedAddresses.set(groupedAddresses);
    });
  }

  writeValue(value: LocationAddressModel) {
    super.writeValue(value);
    this.selectedAddress = value;
  }

  addToFavorites(address: LocationAddressModel, event: Event): void {
    event.stopPropagation();
    const addressToAdd = { ...address, favorite: true };
    this.locationService
      .updateLocationFavorite(addressToAdd)
      .pipe(
        tap((updatedAddress) => {
          this.updateGroupedAddressesWithFavoriteStatus(updatedAddress);
        })
      )
      .subscribe();
  }

  removeFromFavorites(address: LocationAddressModel, event: Event): void {
    event.stopPropagation();
    const addressToRemove = { ...address, favorite: false };
    this.locationService
      .updateLocationFavorite(addressToRemove)
      .pipe(
        tap((updatedAddress) => {
          this.updateGroupedAddressesWithFavoriteStatus(updatedAddress);
        })
      )
      .subscribe();
  }

  private updateGroupedAddressesWithFavoriteStatus(updatedAddress: LocationAddressModel): void {
    const groupedAddresses = this.groupedAddresses();
    const updatedGroupedAddresses = groupedAddresses.map((group) => {
      const updatedAddresses = group.addresses.map((addr) =>
        addr.id === updatedAddress.id ? { ...addr, favorite: updatedAddress.favorite } : addr
      );
      return { ...group, addresses: updatedAddresses };
    });
    this.groupedAddresses.set(updatedGroupedAddresses);
  }
}
